import { App, Flex } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate } from "react-router-dom"

import { CardComponent } from "../../../components/Card/CardComponent.tsx"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { nextPageUrl } from "../../../redux/shared/paged-list.ts"
import type { RootState } from "../../../store.ts"
import { downloadBlob } from "../../../utils/download-file.ts"
import { extractParamsFromNextUrl } from "../../../utils/form/extract-params-from-next-url.ts"
import { serializeData } from "../../../utils/utils"
import {
  updateListProject,
  updateProjectStatus
} from "../../Project/redux/project.thunks.ts"
import { ArchiveProjectModal } from "../components/ArchiveProjectModal.tsx"
import { DEFAULT_PROJECT_ORDERING } from "../constants/created.ts"
import { withoutArchivingStatuses } from "../constants/project-statuses.ts"
import { projectsSetListFilters } from "../redux/projects.slice.ts"
import {
  ProjectFetchType,
  deleteProject,
  fetchImportProjectsTemplates,
  fetchProjects,
  fetchProjectsFilterOptions,
  importProjects,
  restoreProject
} from "../redux/projects.thunks.ts"
import { type ProjectFilters } from "../redux/projects.types.ts"
import type { ProjectListItem } from "../types/types.ts"
import { type ProjectEditableRowData } from "./components/ProjectListEditableRow.tsx"
import { ProjectListTable } from "./components/ProjectListTable.tsx"
import { ProjectListTableFilters } from "./components/ProjectListTableFilters.tsx"
import { ProjectListTableHeader } from "./components/ProjectListTableHeader.tsx"
import { confirmProjectDelete } from "./delete-confirmation.ts"
import { projectRowActionRoute } from "./project-row-action-route.ts"

// The value the backend's project list endpoint expects for the Archive —
// unrelated to routing now that Active and Archive are separate destinations.
const PROJECT_ARCHIVE_VIEW = "archive"

interface ProjectsListPageOwnProps {
  archived: boolean
  /** Mounted inside the Archive page, which supplies its own header and
   *  document title — so this one renders neither and would otherwise stack
   *  a second page header under the Archive's. */
  embedded?: boolean
}

function ProjectsListPageBase({
  archived,
  embedded = false,
  data,
  filters,
  fetching,
  filterOptions,
  projectPermissions,
  updateFilters,
  updateProject,
  updateProjectStatus,
  fetchProjects,
  fetchFilterOptions,
  getImportProjectsTemplates,
  importProjects,
  restoreProject,
  deleteProject
}: ConnectedProps<typeof connector> & ProjectsListPageOwnProps) {
  const navigate = useNavigate()
  const { modal } = App.useApp()
  const { t } = useTranslation()
  const [ordering, setOrdering] = useState<string | null>(
    DEFAULT_PROJECT_ORDERING
  )
  const [projectToArchive, setProjectToArchive] =
    useState<ProjectListItem | null>(null)

  // Which `view` the shared filters slice should be carrying for this mount,
  // and whether it already is. Active and Archive are two mounts of this one
  // component, so entering either has to re-point the slice before any
  // request goes out.
  const targetView = archived ? PROJECT_ARCHIVE_VIEW : null
  const viewAligned = (filters?.view ?? null) === targetView

  const handleGetData = useCallback(() => {
    const serializeFilters = serializeData(filters) as ProjectFilters
    const data = { ordering, ...serializeFilters }
    // An explicit Completed or Cancelled makes the API add the archived rows
    // back, so a stale one would fill the active list with the Archive.
    if (!archived && data.project_status) {
      const kept = withoutArchivingStatuses(data.project_status)
      if (kept) data.project_status = kept
      else delete data.project_status
    }
    // Page one, always: `page` is persisted with the filters, and a scrolled
    // list has no way back to the rows a restored page would skip.
    fetchProjects({
      type: ProjectFetchType.LIST,
      filters: { ...data, page: 1 }
    })
  }, [fetchProjects, filters, ordering, archived])

  const handleOrderingChange = useCallback((next: string | null) => {
    setOrdering(next ?? DEFAULT_PROJECT_ORDERING)
  }, [])

  const handleLoadMore = useCallback(() => {
    const url = nextPageUrl(data)
    if (!url) return
    // The server's own url carries the page and every filter it was built
    // with, so nothing here has to reconstruct them.
    fetchProjects({
      type: ProjectFetchType.LIST,
      isMore: true,
      filters: extractParamsFromNextUrl<ProjectFilters>(url)
    })
  }, [data?.next, data?.retryNext, fetchProjects])

  const handleChangeFilter = useCallback(
    (newData: Partial<ProjectFilters>) => {
      updateFilters({ ...newData, page: 1 })
    },
    [updateFilters]
  )

  const handleRestore = useCallback(
    async (id: string) => {
      await restoreProject(id)
      handleGetData()
    },
    [restoreProject, handleGetData]
  )

  const handleDelete = useCallback(
    (record: ProjectListItem) => {
      confirmProjectDelete({
        modal,
        t,
        record,
        deleteProject,
        onDeleted: handleGetData
      })
    },
    [modal, t, deleteProject, handleGetData]
  )

  const handleStatusChange = async (id: string, project_status: number) => {
    await updateProjectStatus({ project: { id, project_status }, mode: "list" })
    handleGetData()
  }

  const handleUpdateProject = useCallback(
    async (project: ProjectEditableRowData) => {
      await updateProject(project)
      handleGetData()
    },
    [handleGetData, updateProject]
  )

  const handleImportLeads = useCallback(
    async (file: File) => {
      try {
        await importProjects(file).unwrap()
        handleGetData()
      } catch (e) {
        console.log(e)
      }
    },
    [importProjects, handleGetData]
  )

  const handleGetImportLeadsTemplates = useCallback(async () => {
    const blob = await getImportProjectsTemplates()?.unwrap()
    downloadBlob(blob, `import-projects-template.xlsx`)
  }, [getImportProjectsTemplates])

  const handleSelectRow = useCallback(
    (record: ProjectListItem, action: string) => {
      switch (action) {
        case "update":
          return handleUpdateProject(record)
        case "edit":
        case "manage": {
          const route = projectRowActionRoute(action, record.id)
          if (route) navigate(route)
          break
        }
        case "archive":
          return setProjectToArchive(record)
        case "restore":
          return handleRestore(record.id)
        case "delete":
          return handleDelete(record)
      }
    },
    [handleUpdateProject, handleRestore, handleDelete, navigate]
  )

  useEffect(() => {
    // Active and Archive are now separate mounts of this same component
    // (rather than one Segmented switching a shared mount), so this effect is
    // the only place left to align the shared filters slice on entry. Both
    // tabs' narrowing filters are cleared, not just the one being left: the
    // slice merges, and neither tab renders a control for the other's
    // filters — so a survivor is a permanently empty list with no way out.
    if (!viewAligned) {
      updateFilters({
        view: targetView,
        archive_reason: null,
        archived_at_after: null,
        archived_at_before: null,
        project_status: null,
        end_date_after: null,
        end_date_before: null,
        page: 1
      })
    }
  }, [viewAligned, targetView, updateFilters])

  useEffect(() => {
    // Both this and the alignment effect above run in the same commit, so
    // firing unconditionally would send one request built from the *previous*
    // mount's filters — the active list's rows, rendered under the archive's
    // columns, before the real archive request replaced them. Wait for the
    // slice to agree with this mount before asking the server anything.
    if (!viewAligned) return
    handleGetData()
  }, [handleGetData, viewAligned])

  useEffect(() => {
    if (!embedded) generateDocumentTitle("Projects")
    fetchFilterOptions()
  }, [fetchFilterOptions, embedded])

  return (
    <div className={"grid"}>
      <Flex gap={"middle"} vertical className={"overflow-auto"}>
        {!embedded && (
          <ProjectListTableHeader
            projectPermissions={projectPermissions}
            search={filters?.q || ""}
            onImport={handleImportLeads}
            onFilersChange={handleChangeFilter}
            onRefetchProjectsCallback={handleGetData}
            onDownloadTemplate={handleGetImportLeadsTemplates}
          />
        )}

        <CardComponent classNames={{ body: "p-0" }}>
          <ProjectListTableFilters
            filters={filters}
            filterOptions={filterOptions}
            archived={archived}
            onFilersChange={handleChangeFilter}
          />

          <ProjectListTable
            loading={fetching}
            projects={data?.results || []}
            ordering={ordering}
            total={data?.count || 0}
            filters={filters}
            projectPermissions={projectPermissions}
            filterOptions={filterOptions}
            archived={archived}
            onStatusChange={handleStatusChange}
            onSelectRow={handleSelectRow}
            onOrderingChange={handleOrderingChange}
            next={data?.next}
            retryNext={data?.retryNext}
            onLoadMore={handleLoadMore}
          />
        </CardComponent>
      </Flex>

      {projectToArchive && (
        <ArchiveProjectModal
          open
          projectId={projectToArchive.id}
          projectName={projectToArchive.lead_name}
          onCancel={() => setProjectToArchive(null)}
          onArchived={() => {
            setProjectToArchive(null)
            handleGetData()
          }}
        />
      )}
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  data: state.projects.listData,
  filters: state.projects.listFilters,
  fetching: state.projects.fetching || state.projects.fetchingFilterOptions,
  filterOptions: state.projects.filterOptions,
  projectPermissions: state.auth.permissions?.codes?.projects
})

const mapDispatchToProps = {
  fetchProjects,
  updateProjectStatus,
  updateProject: updateListProject,
  fetchFilterOptions: fetchProjectsFilterOptions,
  updateFilters: projectsSetListFilters,
  importProjects,
  restoreProject,
  deleteProject,
  getImportProjectsTemplates: fetchImportProjectsTemplates
}

const connector = connect(mapStateToProps, mapDispatchToProps)

// Active and Archive used to be one Segmented-driven view; they're now two
// separate mounts of the exact same base component, so every filter, column,
// action and style stays byte-for-byte identical between the two.
function ProjectsListPage(props: ConnectedProps<typeof connector>) {
  return <ProjectsListPageBase {...props} archived={false} />
}

export function ProjectsArchivePage(
  props: ConnectedProps<typeof connector> & { embedded?: boolean }
) {
  return <ProjectsListPageBase {...props} archived />
}

export default connector(ProjectsListPage)
export const ConnectedProjectsArchivePage = connector(ProjectsArchivePage)
